// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Progress
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  PERFECT_SCROLLBAR_MAX_100,
  PERFECT_SCROLLBAR_MAX_300
} from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardSecurity.json";

import "react-table/react-table.css";

function SecuritySocDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const intl = useIntl();

  const scoreRadialOptions = useMemo(() => {
    const c = colors;
    const score = data.kpis.securityScore;
    return {
      chart: { sparkline: { enabled: true } },
      plotOptions: {
        radialBar: {
          hollow: { size: "62%" },
          track: { background: c.separatorColor },
          dataLabels: {
            name: { show: true, fontSize: "13px", color: c.primaryColor },
            value: {
              fontSize: "28px",
              fontWeight: 600,
              color: c.primaryColor,
              formatter: () => `${score}`
            }
          }
        }
      },
      labels: ["Score"],
      colors: [c.ReactifyChartColor1]
    };
    }, [colors]);

  const vulnStackOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, stacked: true },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor5, c.ReactifyChartColor2],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.vulnBySeverity.map(v => v.severity),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      legend: { position: "top" },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const vulnTrendOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 3], curve: "smooth" },
      colors: [c.ReactifyChartColor5, c.ReactifyChartColor3],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.vulnTrend.weeks,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const regionOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor5, c.ReactifyChartColor4],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.loginsByRegion.map(r => r.region),
        labels: {
          style: { colors: c.primaryColor },
          rotate: -20,
          rotateAlways: true
        }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      legend: { position: "top" },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const vulnColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "sec.col.cve" }),
        accessor: "cve",
        Cell: p => <code className="small">{p.value}</code>
      },
      { Header: formatMessage({ id: "sec.col.asset" }), accessor: "asset" },
      {
        Header: formatMessage({ id: "sec.col.cvss" }),
        accessor: "cvss",
        Cell: p => (
          <Badge color={p.value >= 9 ? "danger" : "warning"}>{p.value}</Badge>
        )
      },
      { Header: formatMessage({ id: "sec.col.sla" }), accessor: "sla" },
      {
        Header: formatMessage({ id: "sec.col.age" }),
        accessor: "ageHrs",
        Cell: p => `${p.value}h`
      }
    ];
    }, [intl]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--security">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.security" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx lg="8" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3 d-flex flex-wrap justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">
                      <IntlMessages id="sec.hero.title" />
                    </h6>
                    <p className="text-muted small mb-0">
                      <IntlMessages id="sec.hero.sub" />
                    </p>
                  </div>
                  <div className="mt-2 mt-lg-0">
                    <Button color="primary" size="sm" className="me-2">
                      <IntlMessages id="sec.hero.scan" />
                    </Button>
                    <Button color="secondary" size="sm" outline>
                      <IntlMessages id="sec.hero.report" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <p className="text-muted mb-2 small text-uppercase">
                    <IntlMessages id="sec.section.threat" />
                  </p>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_100}>
                    <ul className="list-unstyled mb-0 small">
                      {data.threatFeed.map((t, i) => (
                        <li key={i} className="mb-2">
                          <Badge
                            color={t.severity === "high" ? "danger" : "warning"}
                            className="me-2"
                          >
                            {t.severity}
                          </Badge>
                          {t.title}
                          <span className="text-muted ms-1">({t.age})</span>
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx md="6" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody className="text-center">
                  <p className="text-muted small text-uppercase mb-2">
                    <IntlMessages id="sec.kpi.score" />
                  </p>
                  <Chart
                    options={scoreRadialOptions}
                    series={[k.securityScore]}
                    type="radialBar"
                    height={220}
                    width="100%"
                  />
                  <span className="text-success small">
                    +{k.scoreDelta}{" "}
                    <IntlMessages id="sec.kpi.score-delta" />
                  </span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="6" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-bug text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="sec.kpi.vulns" />
                  </p>
                  <h4 className="mb-0">{k.openVulns}</h4>
                  <span className="text-warning small">
                    {k.criticalOpen}{" "}
                    <IntlMessages id="sec.kpi.critical" />
                  </span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="6" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-wrench text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="sec.kpi.patch" />
                  </p>
                  <h4 className="mb-0">{k.meanTimeToPatchDays}d</h4>
                  <span className="text-success small">{k.patchDelta}d</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="6" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-shield text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="sec.kpi.policies" />
                  </p>
                  <h4 className="mb-0">{k.policiesCompliantPct}%</h4>
                  <p className="text-muted small mb-0">
                    {addCommas(k.failedLogins24h)}{" "}
                    <IntlMessages id="sec.kpi.logins" />
                  </p>
                  <Badge color={k.incidentsOpen > 0 ? "warning" : "success"} className="mt-2">
                    {k.incidentsOpen}{" "}
                    <IntlMessages id="sec.kpi.incidents" />
                  </Badge>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="sec.chart.vuln-stack" />
                  </CardTitle>
                  <Chart
                    options={vulnStackOptions}
                    series={[
                      {
                        name: "Open",
                        data: data.vulnBySeverity.map(v => v.open)
                      },
                      {
                        name: "Remediated (30d)",
                        data: data.vulnBySeverity.map(v => v.remediated30d)
                      }
                    ]}
                    type="bar"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="sec.chart.vuln-trend" />
                  </CardTitle>
                  <Chart
                    options={vulnTrendOptions}
                    series={[
                      { name: "Opened", data: data.vulnTrend.opened },
                      { name: "Closed", data: data.vulnTrend.closed }
                    ]}
                    type="line"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="7" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="sec.chart.regions" />
                  </CardTitle>
                  <Chart
                    options={regionOptions}
                    series={[
                      {
                        name: "Failed logins",
                        data: data.loginsByRegion.map(r => r.failed)
                      },
                      {
                        name: "Blocked",
                        data: data.loginsByRegion.map(r => r.blocked)
                      }
                    ]}
                    type="bar"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="sec.section.policies" />
                  </CardTitle>
                  {data.policies.map((p, i) => (
                    <div key={i} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="small font-weight-medium">{p.name}</span>
                        <span className="text-muted small">{p.owner}</span>
                      </div>
                      <Progress value={p.compliantPct} />
                      <div className="text-right small text-muted">
                        {p.compliantPct}%
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="sec.table.vulns" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.topVulns}
                    columns={vulnColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="sec.table.audit" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_300}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>
                            <IntlMessages id="sec.col.time" />
                          </th>
                          <th>
                            <IntlMessages id="sec.col.actor" />
                          </th>
                          <th>
                            <IntlMessages id="sec.col.action" />
                          </th>
                          <th>
                            <IntlMessages id="sec.col.target" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.auditLog.map((row, i) => (
                          <tr key={i}>
                            <td className="text-muted text-nowrap">{row.t}</td>
                            <td>{row.actor}</td>
                            <td>
                              <code className="small">{row.action}</code>
                            </td>
                            <td>{row.target}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default SecuritySocDashboard;
